import z from 'zod'
import type { AutomationSettings } from './automation/settings'
import type { SigningService } from './esign'
import { AddressSchema, landlineType, mobileOrPhoneType, mobileType, State } from './generic'

const BankAccountSchema = z.object({
    name: z.string(),
    bank: z.string(),
    bsb: z.string(),
    accountNumber: z.string(),
})

export const OrgSettingsSchema = z.object({
    tradingName: z.string().min(1, 'Trading name is required'),
    licenseeName: z.string(),
    licenceNumber: z.string().min(1, 'Licence number is required'),
    licenceExpiry: z.string(),
    abn: z.string()
        .min(1, 'ABN is required')
        .refine(val => /^\d{11}$/.test(val.replaceAll(' ', '')), 'ABN must be 11 digits'),
    acn: z.string()
        .refine(val => !val || /^\d{9}$/.test(val.replaceAll(' ', '')), 'ACN must be 9 digits')
        .optional(),
    address: AddressSchema.optional(),
    phone: mobileOrPhoneType,
    mobileNumber: z.string()
        .refine(val => !val || mobileType.safeParse(val).success, 'Invalid mobile number')
        .optional(),
    fax: z.string()
        .refine(val => !val || landlineType.safeParse(val).success, 'Invalid fax number')
        .optional(),
    email: z.string().email('Please enter a valid email'),
    website: z.string()
        .refine(val => !val || /^(https?:\/\/)?[\w.-]+\.\w{2,}(\/\S*)?$/.test(val), 'Please enter a valid website URL')
        .optional(),
})

export type OrgSettingsSchema = z.infer<typeof OrgSettingsSchema>

export interface OrgBilling {
    stripeCustomerId?: string
    stripeSubscriptionId?: string
    stripeSubscriptionItemId?: string
    billingEmail?: string
    stripeSubscription?: Record<string, any>
}

export interface TourPausedAt {
    stepKey: string
    subStep: number
    reason: 'oauth' | 'new-tab'
}

export interface OnboardingProgress {
    completed: boolean
    currentStep?: number
    invoiceStepSeen?: boolean
    tourPausedAt?: TourPausedAt | null
}

export interface InternalOrgSchema {
    orgId: string
    orgName: string
    state: State
    logo?: string
    xeroConnected?: boolean
    annatureGroupId?: string
    docusignConnected?: boolean
    signingService?: SigningService
    billing?: OrgBilling
    onboarding?: OnboardingProgress
    automationSettings?: AutomationSettings
    // Set at signup when the org was referred by an affiliate (see `referral` table).
    referredByAffiliateId?: string
}
